/**
 * Workday clock — spec: specs/task-dependencies-workday.md
 *
 * This module is stateful: one active calendar lives at module level.
 * Bootstrap it once at app start via setActiveWorkdayCalendar(cal); all the
 * pure helpers below (addWorkingMinutes, diffWorkingMinutes, etc.) then
 * honor its per-day working_times + holiday list. If never set, the
 * spec defaults (10h workday, 07–17, Mon–Fri) are used.
 */

import type { NonWorkingDay, ScheduleCalendar, WorkingTime, WorkingTimes } from "./api"

// Spec defaults — used until setActiveWorkdayCalendar is called.
const SPEC_DEFAULT_WT: WorkingTimes = {
  mon: { start: "07:00", end: "17:00" },
  tue: { start: "07:00", end: "17:00" },
  wed: { start: "07:00", end: "17:00" },
  thu: { start: "07:00", end: "17:00" },
  fri: { start: "07:00", end: "17:00" },
  sat: null,
  sun: null,
}

let activeCalendar: ScheduleCalendar | null = null

/** Bootstrap the active calendar — typically called once at app load. */
export function setActiveWorkdayCalendar(cal: ScheduleCalendar | null): void {
  activeCalendar = cal
}

export function getActiveWorkdayCalendar(): ScheduleCalendar | null {
  return activeCalendar
}

// Lookup keys 0 (Sun) → 6 (Sat)
const DAY_KEYS: (keyof WorkingTimes)[] = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"]

function parseHM(s: string): { h: number; m: number } {
  const [h, m] = s.split(":").map(n => parseInt(n, 10))
  return { h: isNaN(h) ? 0 : h, m: isNaN(m) ? 0 : m }
}

function currentWorkingTimes(): WorkingTimes {
  return activeCalendar?.working_times || SPEC_DEFAULT_WT
}

/**
 * True if a non-working / exception entry covers the given YYYY-MM-DD.
 * Supports an optional inclusive `end_date` range and annual recurrence
 * (matched by month-day; a recurring range may wrap the year boundary, e.g.
 * Dec 24 → Jan 2). A single-day entry has `end_date` blank/undefined.
 */
export function calendarEntryCoversISO(entry: NonWorkingDay, iso: string): boolean {
  if (!entry?.date) return false
  const start = entry.date
  const end = entry.end_date && entry.end_date >= start ? entry.end_date : start
  if (!entry.recurring) return iso >= start && iso <= end
  const sMD = start.slice(5), eMD = end.slice(5), dMD = iso.slice(5)
  return sMD <= eMD ? (dMD >= sMD && dMD <= eMD) : (dMD >= sMD || dMD <= eMD)
}

const pad = (n: number) => String(n).padStart(2, "0")
// Local Y/M/D, not toISOString() — the latter converts to UTC, which shifts
// evening wall-clock times (e.g. 22:00 in a UTC-5 zone) onto the next
// calendar day and misses holiday/blackout entries keyed on the local date.
const localISO = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`

/** Window for the given date (null = not a working day). */
function windowFor(d: Date): WorkingTime | null {
  // Holiday / non-working day list takes precedence.
  const iso = localISO(d)
  if (activeCalendar) {
    for (const nwd of activeCalendar.non_working_days || []) {
      if (calendarEntryCoversISO(nwd, iso)) return null
    }
    // Workday exception forces a working day — fall through to working_times lookup.
    // (The exception says "yes it's a work day", so we use the normal day-of-week window.)
  }
  return currentWorkingTimes()[DAY_KEYS[d.getDay()]]
}

// Back-compat: old code imports these constants. They reflect the DEFAULT
// calendar, which may differ from the active one once loaded.
export const WORKDAY_START_HOUR = 7
export const WORKDAY_END_HOUR = 17
export const WORKDAY_MINUTES = (WORKDAY_END_HOUR - WORKDAY_START_HOUR) * 60 // 600

export function isWorkingDay(d: Date): boolean {
  return windowFor(d) !== null
}

/** Advance d to the next working day's shop start. Mutates and returns. */
function advanceToNextWorkingDayStart(d: Date): void {
  d.setDate(d.getDate() + 1)
  for (let i = 0; i < 30; i++) {
    const w = windowFor(d)
    if (w) {
      const { h, m } = parseHM(w.start)
      d.setHours(h, m, 0, 0)
      return
    }
    d.setDate(d.getDate() + 1)
  }
  // Safety fallback — don't infinite-loop if the calendar has zero working days.
  d.setHours(7, 0, 0, 0)
}

/** Return the given date clamped to the shop start of a working day.
 *  If already on a working day, uses that day at its own start time.
 *  Otherwise advances to the next working day. */
export function alignToShopStart(d: Date): Date {
  const out = new Date(d)
  const w = windowFor(out)
  if (w) {
    const { h, m } = parseHM(w.start)
    out.setHours(h, m, 0, 0)
    return out
  }
  advanceToNextWorkingDayStart(out)
  return out
}

/**
 * Add `workMinutes` of working time to `start`, honoring the active
 * calendar's per-day windows and holiday list. Returns the resulting Date,
 * possibly spanning multiple working days.
 */
export function addWorkingMinutes(start: Date, workMinutes: number): Date {
  if (workMinutes <= 0) return new Date(start)
  const cur = new Date(start)
  let remaining = workMinutes
  let iterations = 0

  while (remaining > 0 && iterations++ < 1000) {
    const win = windowFor(cur)
    if (!win) {
      advanceToNextWorkingDayStart(cur)
      continue
    }
    const { h: sh, m: sm } = parseHM(win.start)
    const { h: eh, m: em } = parseHM(win.end)
    const openMins = sh * 60 + sm
    const closeMins = eh * 60 + em
    const nowMins = cur.getHours() * 60 + cur.getMinutes()

    if (nowMins < openMins) {
      cur.setHours(sh, sm, 0, 0)
      continue
    }
    if (nowMins >= closeMins) {
      advanceToNextWorkingDayStart(cur)
      continue
    }
    const minutesUntilClose = closeMins - nowMins
    if (remaining <= minutesUntilClose) {
      cur.setMinutes(cur.getMinutes() + remaining)
      remaining = 0
    } else {
      remaining -= minutesUntilClose
      advanceToNextWorkingDayStart(cur)
    }
  }
  return cur
}

/** Retreat d to the previous working day's shop close. Mutates and returns. */
function retreatToPrevWorkingDayEnd(d: Date): void {
  d.setDate(d.getDate() - 1)
  for (let i = 0; i < 30; i++) {
    const w = windowFor(d)
    if (w) {
      const { h, m } = parseHM(w.end)
      d.setHours(h, m, 0, 0)
      return
    }
    d.setDate(d.getDate() - 1)
  }
  // Safety fallback — don't infinite-loop if the calendar has zero working days.
  d.setHours(17, 0, 0, 0)
}

/**
 * Subtract `workMinutes` of working time from `end`, honoring the active
 * calendar's per-day windows and holiday list. Inverse of addWorkingMinutes —
 * returns the resulting start Date, possibly spanning multiple working days.
 */
export function subtractWorkingMinutes(end: Date, workMinutes: number): Date {
  if (workMinutes <= 0) return new Date(end)
  const cur = new Date(end)
  let remaining = workMinutes
  let iterations = 0

  while (remaining > 0 && iterations++ < 1000) {
    const win = windowFor(cur)
    if (!win) {
      retreatToPrevWorkingDayEnd(cur)
      continue
    }
    const { h: sh, m: sm } = parseHM(win.start)
    const { h: eh, m: em } = parseHM(win.end)
    const openMins = sh * 60 + sm
    const closeMins = eh * 60 + em
    const nowMins = cur.getHours() * 60 + cur.getMinutes()

    if (nowMins > closeMins) {
      cur.setHours(eh, em, 0, 0)
      continue
    }
    if (nowMins <= openMins) {
      retreatToPrevWorkingDayEnd(cur)
      continue
    }
    const minutesUntilOpen = nowMins - openMins
    if (remaining <= minutesUntilOpen) {
      cur.setMinutes(cur.getMinutes() - remaining)
      remaining = 0
    } else {
      remaining -= minutesUntilOpen
      retreatToPrevWorkingDayEnd(cur)
    }
  }
  return cur
}

/**
 * Diff two dates in working minutes (inverse of addWorkingMinutes).
 * Returns 0 if end <= start.
 */
export function diffWorkingMinutes(start: Date, end: Date): number {
  if (end.getTime() <= start.getTime()) return 0
  let total = 0
  const cur = new Date(start)
  let iterations = 0
  while (cur.getTime() < end.getTime() && iterations++ < 1000) {
    const win = windowFor(cur)
    if (!win) {
      advanceToNextWorkingDayStart(cur)
      continue
    }
    const { h: sh, m: sm } = parseHM(win.start)
    const { h: eh, m: em } = parseHM(win.end)
    const openMins = sh * 60 + sm
    const closeMins = eh * 60 + em
    const nowMins = cur.getHours() * 60 + cur.getMinutes()
    if (nowMins < openMins) {
      cur.setHours(sh, sm, 0, 0)
      continue
    }
    if (nowMins >= closeMins) {
      advanceToNextWorkingDayStart(cur)
      continue
    }
    // cur is in the shop window — contribute up to end or close.
    const closeToday = new Date(cur)
    closeToday.setHours(eh, em, 0, 0)
    const stop = end.getTime() < closeToday.getTime() ? end : closeToday
    total += Math.floor((stop.getTime() - cur.getTime()) / 60000)
    cur.setTime(closeToday.getTime())
    advanceToNextWorkingDayStart(cur)
  }
  return total
}

/**
 * Format workday minutes as a human string: "1d 2h 30m".
 * 1 workday = WORKDAY_MINUTES (600) by the current default.
 */
export function formatWorkdayDuration(minutes: number): string {
  if (!minutes || minutes <= 0) return ""
  const days = Math.floor(minutes / WORKDAY_MINUTES)
  const rem = minutes % WORKDAY_MINUTES
  const hours = Math.floor(rem / 60)
  const mins = rem % 60
  const parts: string[] = []
  if (days > 0) parts.push(`${days}d`)
  if (hours > 0) parts.push(`${hours}h`)
  if (mins > 0) parts.push(`${mins}m`)
  return parts.join(" ")
}

/** Parse human duration ("1d 2h 30m", "4h", "90m") → workday minutes. */
export function parseWorkdayDuration(input: string): number | null {
  const s = input.trim().toLowerCase()
  if (!s) return null
  let total = 0
  let matched = false
  const dayMatch = s.match(/(\d+)\s*d/)
  if (dayMatch) {
    total += parseInt(dayMatch[1]) * WORKDAY_MINUTES
    matched = true
  }
  const hrMatch = s.match(/(\d+)\s*h/)
  if (hrMatch) {
    total += parseInt(hrMatch[1]) * 60
    matched = true
  }
  const minMatch = s.match(/(\d+)\s*m(?!o)/)
  if (minMatch) {
    total += parseInt(minMatch[1])
    matched = true
  }
  return matched && total > 0 ? total : null
}

/** Parse a datetime-local string ("YYYY-MM-DDTHH:mm") into a Date, or null. */
export function parseDatetimeLocal(s: string): Date | null {
  if (!s) return null
  // datetime-local is treated as local time
  const d = new Date(s)
  return isNaN(d.getTime()) ? null : d
}

/** Format a Date as "YYYY-MM-DDTHH:mm" for <input type="datetime-local">. */
export function toDatetimeLocal(d: Date | null | undefined): string {
  if (!d) return ""
  const pad = (n: number) => String(n).padStart(2, "0")
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/**
 * Run `fn` with `cal` temporarily installed as the active calendar, then
 * restore the previous one. Lets callers recompute task math under a different
 * calendar without leaving the module's global state mutated. Sync only —
 * don't await inside `fn` or the restoration will race.
 */
export function withCalendar<T>(cal: ScheduleCalendar | null, fn: () => T): T {
  const prev = activeCalendar
  activeCalendar = cal
  try {
    return fn()
  } finally {
    activeCalendar = prev
  }
}

/**
 * Recompute a task's end timestamp under a (possibly different) calendar,
 * preserving its start and its workday-minutes duration. Returns the new end
 * as a Date, or null if the task doesn't have enough info to recalc (no
 * start, or no parseable duration).
 *
 * Used by the project / settings warn-and-recalc flows when a calendar swap
 * shifts working hours and the existing due_date no longer reflects "this
 * task takes N working minutes".
 */
export function recalcTaskEndUnderCalendar(
  task: { start_date?: string | null; due_date?: string | null; duration_minutes?: number | null },
  cal: ScheduleCalendar | null,
): Date | null {
  if (!task.start_date) return null
  const start = new Date(task.start_date)
  if (isNaN(start.getTime())) return null
  let mins = task.duration_minutes ?? null
  // Fallback: derive duration from existing start/end under the OLD calendar.
  // (Caller hasn't installed the new calendar yet — we use the active one.)
  if ((mins == null || mins <= 0) && task.due_date) {
    const oldEnd = new Date(task.due_date)
    if (!isNaN(oldEnd.getTime())) {
      mins = diffWorkingMinutes(start, oldEnd)
    }
  }
  if (!mins || mins <= 0) return null
  return withCalendar(cal, () => {
    const aligned = alignToShopStart(start)
    return addWorkingMinutes(aligned, mins!)
  })
}
